import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TimeSlotButton } from "@/components/admin-portal/appointment/form/date-time";
import { useDateContext } from "@/components/providers/date-provider";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useTherapistSlots } from "@/hooks/admin-portal/scheduling/use-therapist-slots";
import {
	ACTIVE_SLOT_SOURCE,
	SLOT_SOURCE,
} from "@/lib/therapist-scheduling/constants";
import { formatSlotTime, toIsoDate } from "@/lib/therapist-scheduling/date";
import { splitQuickSlots } from "@/lib/therapist-scheduling/quick-slots";
import type {
	AvailableTherapist,
	TimeSlot,
} from "@/lib/therapist-scheduling/types";

type TherapistSlotListProps = {
	therapist: AvailableTherapist;
	date: Date;
	selectedStartTime?: string | null;
	onSelectSlot: (slot: TimeSlot) => void;
};

/**
 * Slots for a single therapist, shown as clickable time blocks styled like the
 * legacy "cari terapis" quick-time row. Slot source is the `ACTIVE_SLOT_SOURCE` flag:
 * AVAILABLE_ONLY uses the embedded list slots; PER_THERAPIST_DETAIL fetches the
 * richer /slots endpoint. Slots are shown as returned — no notice or past gating
 * (admin freedom). Labels follow the active language + time format.
 *
 * Only a handful of anchor-spread slots sit inline; the rest live behind a
 * popover, so a therapist with 20+ slots does not push the availability list
 * past its scroller. See `splitQuickSlots`.
 */
export function TherapistSlotList({
	therapist,
	date,
	selectedStartTime,
	onSelectSlot,
}: TherapistSlotListProps) {
	const { locale, tzDate } = useDateContext();
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.schedule_picker.slot",
	});
	const [isMoreOpen, setIsMoreOpen] = useState(false);
	const detailMode = ACTIVE_SLOT_SOURCE === SLOT_SOURCE.PER_THERAPIST_DETAIL;
	const iso = toIsoDate(date);
	const { data: detail } = useTherapistSlots(
		detailMode ? therapist.id : null,
		detailMode ? iso : null,
	);

	const slots: TimeSlot[] = detailMode
		? (detail?.data.available_slots ?? []).map((s) => ({
				start_time: s.start_time,
				end_time: s.end_time,
			}))
		: (therapist.available_slots ?? []);

	const { quick, rest } = useMemo(
		() => splitQuickSlots(slots, selectedStartTime),
		[slots, selectedStartTime],
	);

	const renderSlot = (slot: TimeSlot, onSelect: () => void) => {
		const isSelected = selectedStartTime === slot.start_time;

		return (
			<TimeSlotButton
				key={slot.start_time}
				time={slot.start_time}
				label={formatSlotTime(slot.start_time, locale, tzDate)}
				isSelected={isSelected}
				ariaPressed={isSelected}
				onSelect={onSelect}
			/>
		);
	};

	if (slots.length === 0) {
		return <p className="text-muted-foreground text-xs">{t("empty")}</p>;
	}

	// `[&>button]:w-auto` undoes TimeSlotButton's w-full so each chip takes its
	// content width and the row wraps instead of stacking.
	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-wrap gap-2 [&>button]:w-auto">
				{quick.map((slot) => renderSlot(slot, () => onSelectSlot(slot)))}
			</div>

			{rest.length > 0 && (
				<Popover onOpenChange={setIsMoreOpen} open={isMoreOpen}>
					<PopoverTrigger asChild>
						<Button
							className="w-fit gap-1.5 px-2 text-muted-foreground text-xs"
							size="sm"
							type="button"
							variant="ghost"
						>
							{t("more", { count: rest.length })}
							<ChevronDown className="size-3.5 shrink-0" />
						</Button>
					</PopoverTrigger>

					<PopoverContent align="start" className="w-64 p-2">
						<p className="px-1 pb-2 font-medium text-muted-foreground text-xs">
							{t("more_title")}
						</p>
						{/* Caps at ~5 rows; longer days scroll inside the popover rather
						    than growing it past the viewport. */}
						<div className="grid max-h-56 grid-cols-3 gap-1.5 overflow-y-auto [&>button]:w-full">
							{rest.map((slot) =>
								renderSlot(slot, () => {
									onSelectSlot(slot);
									setIsMoreOpen(false);
								}),
							)}
						</div>
					</PopoverContent>
				</Popover>
			)}
		</div>
	);
}
